import React from "react"
import PropTypes from "prop-types"
import { Bar } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import Config from "assets/configs/score"
import { COLORS } from "utils/constants/colors"
import { DEFAULT_OPTIONS } from "services/charts/default"
import { DEFAULT_PLUGINS } from "services/charts/plugins/default"
import { dataLabelsPlugin } from "services/charts/plugins/datalabeles"
import NodeOrganization from "services/organizations/NodeOrganization"
import { round } from "services/units/round"
import { ChartContainer } from "views/organisms/organization/tables/ChartContainer"

const TRANSLATION_PREFIX = "score:score.13.chartCo2AndWebsitesMaturityConsolidated"

const ChartODD13Co2AndWebsitesMaturityConsolidatedMolecule = ({ score }) => {
  const { t } = useTranslation()
  const { datasets = [], unit_key: unitKey } =
    score?.odd13_co2_and_websites_maturity_consolidated || {}

  const labels = datasets.map((dataset) =>
    NodeOrganization.getNodeNameWithVersion(dataset.label_key)
  )
  const emissionUnit = t(`units:units.emission.${unitKey}`)
  const maturityUnit = "%"

  const data = {
    labels,
    datasets: [
      {
        label: t(`${TRANSLATION_PREFIX}.legend.co2`),
        data: datasets.map((dataset) => round(dataset.co2)),
        backgroundColor: Config.common.colors[0],
        borderColor: COLORS.white,
        borderWidth: 1,
        type: "bar",
        yAxisID: "y-axis-1",
        order: 3,
        datalabels: {
          color: COLORS.white,
          font: { weight: "bold" },
        },
      },
      {
        label: t(`${TRANSLATION_PREFIX}.legend.ecodesign_maturity`),
        data: datasets.map((dataset) => dataset.ecodesign_maturity),
        borderColor: Config.common.colors[1],
        backgroundColor: Config.common.colors[1],
        borderWidth: 2,
        fill: false,
        type: "line",
        yAxisID: "y-axis-2",
        order: 1,
        datalabels: { display: false },
        spanGaps: false,
      },
      {
        label: t(`${TRANSLATION_PREFIX}.legend.accessibility_maturity`),
        data: datasets.map((dataset) => dataset.accessibility_maturity),
        borderColor: Config.common.colors[2],
        backgroundColor: Config.common.colors[2],
        borderWidth: 2,
        fill: false,
        type: "line",
        yAxisID: "y-axis-2",
        order: 2,
        datalabels: { display: false },
        spanGaps: false,
      },
    ],
  }

  const options = {
    ...DEFAULT_OPTIONS,
    legend: {
      display: true,
      position: "bottom",
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, chartData) => {
          const dataset = chartData.datasets[tooltipItem.datasetIndex]
          const value = dataset.data[tooltipItem.index]
          const unit = dataset.yAxisID === "y-axis-1" ? emissionUnit : maturityUnit
          return `${dataset.label}: ${value ?? "-"} ${value != null ? unit : ""}`.trim()
        },
      },
    },
    scales: {
      yAxes: [
        {
          type: "linear",
          display: true,
          position: "right",
          id: "y-axis-1",
          ticks: { beginAtZero: true },
          scaleLabel: {
            display: true,
            labelString: emissionUnit,
          },
        },
        {
          type: "linear",
          display: true,
          position: "left",
          id: "y-axis-2",
          ticks: {
            beginAtZero: true,
            max: 100,
          },
          scaleLabel: {
            display: true,
            labelString: maturityUnit,
          },
        },
      ],
    },
    plugins: dataLabelsPlugin(),
  }

  return (
    <ChartContainer
      title={t(`${TRANSLATION_PREFIX}.title`)}
      helpText={t(`${TRANSLATION_PREFIX}.help`)}
    >
      <Bar options={options} data={data} plugins={DEFAULT_PLUGINS} />
    </ChartContainer>
  )
}

ChartODD13Co2AndWebsitesMaturityConsolidatedMolecule.propTypes = {
  score: PropTypes.object.isRequired,
}

export default ChartODD13Co2AndWebsitesMaturityConsolidatedMolecule
